import { parseISO, isSameDay, startOfDay, getMinutes, getHours, format } from 'date-fns'
import { cloneDeep } from 'lodash'
import { Database } from 'merinio-types'

const durationInMinutes = (start, end) => Math.floor((end.getTime() - start.getTime()) / 60000)
const isntMidnight = time => !(getMinutes(time) === 0 && getHours(time) === 0)

export function splitShifts(shifts: Database.Schedule[]): Database.Schedule[] {
  const splitShifts: Database.Schedule[] = []

  for (const shift of shifts) {
    const shiftStart = parseISO(shift.start)
    const shiftEnd = parseISO(shift.end)

    // Handle special case for midnight since it isn't technically the next day
    if (!isSameDay(shiftStart, shiftEnd) && isntMidnight(shiftEnd)) {
      // Split the shift at midnight, which will show up as the next day
      const splitTime = startOfDay(shiftEnd)
      const splitTimeFormatted = format(splitTime, `yyyy-MM-dd'T'HH:mm:ss`)

      // First part of shift
      const firstDuration = durationInMinutes(shiftStart, splitTime)
      splitShifts.push({ ...cloneDeep(shift), end: splitTimeFormatted, duration: firstDuration })

      // Last part of shift
      const lastDuration = durationInMinutes(splitTime, shiftEnd)
      splitShifts.push({ ...cloneDeep(shift), breaks: [], start: splitTimeFormatted, duration: lastDuration })
    } else {
      const duration = durationInMinutes(shiftStart, shiftEnd)
      splitShifts.push({ ...cloneDeep(shift), duration })
    }
  }

  return splitShifts
}

export type SplitPunchCard = Database.PunchCard & { splitPart?: number }

export function splitPunchCards(punchCards: SplitPunchCard[]): SplitPunchCard[] {
  const splitPunchCards: SplitPunchCard[] = []

  for (const punchCard of punchCards) {
    if (!punchCard.arrivalRounded || !punchCard.leaveRounded) {
      splitPunchCards.push({ ...punchCard })
      continue
    }
    const arrival = parseISO(punchCard.arrivalRounded)
    const leave = parseISO(punchCard.leaveRounded)

    // Handle special case for midnight since it isn't technically the next day
    if (!isSameDay(arrival, leave) && isntMidnight(leave)) {
      // Split the punch card at midnight, which will show up as the next day
      const splitTime = startOfDay(leave)
      const splitTimeFormatted = format(splitTime, `yyyy-MM-dd'T'HH:mm:ss`)

      const [wagePremiumsFirstPart, wagePremiumsSecondPart] = punchCard.wagePremiums.reduce(
        (acc, wagePremium) => {
          const [firstPart, secondPart] = acc

          // If splitPart exists, each wage premium part has a specific part of the punchCard to go on.
          if (wagePremium.splitPart === 1) return [[...firstPart, wagePremium], secondPart]
          if (wagePremium.splitPart === 2) return [firstPart, [...secondPart, wagePremium]]

          if (wagePremium.fullShift) {
            return [
              [...firstPart, wagePremium],
              [...secondPart, wagePremium]
            ]
          }

          if (wagePremium.unitBased) {
            const halfUnits = wagePremium.units / 2
            return [
              [...firstPart, { ...wagePremium, units: halfUnits }],
              [...secondPart, { ...wagePremium, units: halfUnits }]
            ]
          }

          if (typeof wagePremium.hours === 'number') {
            const halfHours = wagePremium.hours / 2
            return [
              [...firstPart, { ...wagePremium, hours: halfHours }],
              [...secondPart, { ...wagePremium, hours: halfHours }]
            ]
          }

          return [[...firstPart, wagePremium], secondPart]
        },
        [[], []] as Database.PunchCard['wagePremiums'][]
      )

      // First part of punch card
      splitPunchCards.push({
        ...cloneDeep(punchCard),
        wagePremiums: wagePremiumsFirstPart,
        splitPart: 1,
        leave: splitTimeFormatted,
        leaveRounded: splitTimeFormatted
      })

      // Last part of punch card
      splitPunchCards.push({
        ...cloneDeep(punchCard),
        wagePremiums: wagePremiumsSecondPart,
        splitPart: 2,
        breaks: [],
        arrival: splitTimeFormatted,
        arrivalRounded: splitTimeFormatted
      })
    } else {
      splitPunchCards.push({ ...cloneDeep(punchCard), splitPart: 1 })
    }
  }

  return splitPunchCards
}
